import * as CM from '@emersya/mpa-components-manager';

interface ICommand<TName extends string, TContext, TReturn = void> {
	name: TName;
	do(context: TContext): Promise<TReturn>;
	undo(context: TContext): Promise<TReturn>;
};

interface IAction<TContext, TReturn> {
	command: ICommand<string, TContext, TReturn>;
	context: TContext;
};

type TRegistry = Record<string, ICommand<string, unknown, unknown>>;

type TCommandInferContext<TCommand> = TCommand extends ICommand<string, infer TContext, any> ? TContext : never;
type TCommandInferReturn<TCommand> = TCommand extends ICommand<string, unknown, infer TReturn> ? TReturn : never;

interface IHistoryManagerComponentConfiguration extends CM.Component.IConfiguration {
	maxHistoryItems: number;
};

class HistoryManagerComponent extends CM.Component<HistoryManagerComponent, IHistoryManagerComponentConfiguration > {
	readonly commandsRegistry: TRegistry = {};
	readonly maxHistoryItems: number;
	readonly stack: IAction<unknown, unknown>[] = [];
	actionIndex: number = 0;
	actionIsDone: boolean = true;
	isBusy: boolean = false;

	registerCommand<TCommand extends ICommand<string, TCommandInferContext<TCommand>>>(command: TCommand) {
		if (command.name in this.commandsRegistry)
			this.component_throwUserError(`Command '${command.name}' is already registered`, {});

		this.commandsRegistry[command.name] = command;
	}

	async execute<TCommand extends ICommand<string, TCommandInferContext<TCommand>, TCommandInferReturn<TCommand>>>(
		commandName: TCommand['name'],
		context: TCommandInferContext<TCommand>
	): Promise<TCommandInferReturn<TCommand>> {
		if (this.isBusy) {
			this.component_throwUserError('HistoryManager is busy', {});
		} else {
			this.isBusy = true;
		}

		this.addExecuted(commandName, context);

		const result = await (<TCommand>this.commandsRegistry[commandName]!).do(context);

		this.isBusy = false;

		return result;
	}

	addExecuted<TCommand extends ICommand<string, unknown>>(
		commandName: TCommand['name'],
		context: TCommandInferContext<TCommand>
	) {
		if (!(commandName in this.commandsRegistry))
			this.component_throwUserError(`Undefined command '${commandName}'`, {});

		const action = {
			command: this.commandsRegistry[commandName]!,
			context
		};

		if (this.actionIndex < this.stack.length - 1)
			this.stack.length = this.actionIndex + 1;

		this.stack.push(action);

		while (this.stack.length > this.maxHistoryItems)
			this.stack.shift();

		this.actionIndex = this.stack.length - 1;
		this.actionIsDone = true;
	}

	async undo() {
		if (this.isBusy || (this.actionIndex === 0 && !this.actionIsDone)) return;

		this.isBusy = true;

		if (!this.actionIsDone && this.actionIndex > 0)
			this.actionIndex--;

		const action = this.stack[this.actionIndex]!;
		await action.command.undo(action.context);

		this.actionIsDone = false;
		this.isBusy = false;
	}

	async redo() {
		if (this.isBusy || this.stack.length === 0 || (this.actionIndex === this.stack.length - 1 && this.actionIsDone)) return;

		this.isBusy = true;

		if (this.actionIsDone && this.actionIndex < this.stack.length - 1)
			this.actionIndex++;

		const action = this.stack[this.actionIndex]!;
		await action.command.do(action.context);

		this.actionIsDone = true;
		this.isBusy = false;
	}

	clear() {
		this.stack.length = 0;
		this.actionIndex = 0;
		this.actionIsDone = true;
	}

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: IHistoryManagerComponentConfiguration
	) {
		if (!configuration.maxHistoryItems) configuration.maxHistoryItems = 15; 
		super(descriptor, configuration);
		this.maxHistoryItems = configuration.maxHistoryItems;
	}
}

export default HistoryManagerComponent;
export namespace NS {
	export const Component = HistoryManagerComponent;
	export type Component = HistoryManagerComponent;
	export type Configuration = IHistoryManagerComponentConfiguration;
	export type Command<TName extends string, TContext = unknown, TReturn = void> = ICommand<TName, TContext, TReturn>;
};

CM.Manager.declare({
	id: 'history-manager',
	type: HistoryManagerComponent
});
